import { makeStyles } from '@material-ui/core';
import { useMemo, memo, useRef, useCallback } from 'react';
import CalendarItem from './CalendarItem';
import { CalendarItemType, CalendarUserType } from './Calendar';
import moment from 'moment';
import { isWeekend, adjustedDayLength, getDragOverlayStyle, countWeekdaysInclusive, weekdayOrdinal, calendarDayFromWeekdayOrdinal } from './utils/calendarPositioning';
import { useDragState, DragCallbacks } from './hooks/useDragState';
import { statusColor } from 'components/Home/Forecast/utils';
import { haptic } from 'utils/haptics';

const useStyles = makeStyles(() => ({
  calendar_row: {
    height: '45px',
    '@media (max-width: 600px)': {
      height: '50px',
    },
    position: 'relative',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    cursor: 'crosshair',
    userSelect: 'none',
    transition: 'background-color 0.1s ease',
  },
  dragOverlay: {
    position: 'absolute',
    top: 0,
    height: '100%',
    backgroundColor: 'rgba(33, 150, 243, 0.2)',
    border: '2px solid #2196F3',
    borderRadius: 4,
    pointerEvents: 'none',
    zIndex: 100,
    transition: 'all 0.02s ease',
  },
  dayGrid: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    display: 'flex',
    pointerEvents: 'none',
    zIndex: 1,
  },
  dayCell: {
    flex: 1,
    height: '100%',
    '&.weekend': {
      flex: 0.15,
    },
  },
}));

const CalendarRow = ({
  user,
  edit,
  startDate,
  length,
  dayPixelWidth,
  isAdmin,
  viewOnly,
  onDragCreate,
  onDragHighlight,
  onDragMove,
  onDragResize,
}: {
  user: CalendarUserType;
  edit: (item: CalendarItemType, userId: string) => void;
  startDate: moment.Moment;
  length: number;
  dayPixelWidth: number;
  isAdmin: boolean;
  viewOnly: boolean;
  onDragCreate?: (userId: string, startDate: Date, endDate: Date) => void;
  onDragHighlight?: (highlight: { startDayIndex: number; endDayIndex: number } | null) => void;
  onDragMove?: (itemId: string, startDate: Date, endDate: Date) => void;
  onDragResize?: (itemId: string, startDate: Date, endDate: Date) => void;
}) => {
  const classes = useStyles();

  // Set up drag callbacks
  const dragCallbacks: DragCallbacks = {
    onDragCreate,
    onDragHighlight,
    onDragMove,
    onDragResize,
  };

  // Use the centralized drag state hook
  const { isDragging, containerRef, startDrag, dragState } = useDragState(startDate, length, dragCallbacks);

  const canDragCreate = (user.isCurrentUser || isAdmin) && !viewOnly && onDragCreate;

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!canDragCreate) return;

    // Check if we're clicking on an existing forecast
    const target = e.target as HTMLElement;
    const isOnExistingForecast = target.closest('.calendar-item') !== null;

    // Don't start drag-to-create if clicking on an existing forecast
    if (isOnExistingForecast) {
      return;
    }

    // Start drag-to-create
    if (user.id) {
      startDrag('create', e.clientX, { userId: user.id });
    }
  };

  // Long-press touch handling for mobile drag-to-create
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  const longPressActive = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!canDragCreate) return;

    const target = e.target as HTMLElement;
    if (target.closest('.calendar-item')) return;

    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };
    longPressActive.current = false;

    longPressTimer.current = setTimeout(() => {
      if (user.id && touchStartPos.current) {
        longPressActive.current = true;
        haptic('medium');
        startDrag('create', touchStartPos.current.x, { userId: user.id });
      }
    }, 150);
  }, [canDragCreate, user.id, startDrag]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    // If drag is active, prevent scrolling
    if (longPressActive.current) {
      e.preventDefault();
      return;
    }
    // If long press hasn't fired yet, check if we should cancel it (user is scrolling)
    if (longPressTimer.current && touchStartPos.current) {
      const touch = e.touches[0];
      const dx = touch.clientX - touchStartPos.current.x;
      const dy = touch.clientY - touchStartPos.current.y;
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    longPressActive.current = false;
  }, []);

  // Handler for drag-to-move
  const handleMouseDownMove = (
    e: React.MouseEvent,
    itemId: string,
    originalStartDay: number,
    originalEndDay: number,
    status?: string
  ) => {
    if (!itemId) return;
    // Calculate the number of weekdays in the original forecast
    const originalStartDate = moment(startDate).add(originalStartDay, 'days');
    const originalEndDate = moment(startDate).add(originalEndDay, 'days');
    const originalWeekdays = countWeekdaysInclusive(originalStartDate, originalEndDate);
    startDrag('move', e.clientX, { itemId, originalStartDay, originalEndDay, draggedStatus: status, originalWeekdays });
  };

  // Handler for resize operations
  const handleMouseDownResize = (
    e: React.MouseEvent,
    itemId: string,
    originalStartDay: number,
    originalEndDay: number,
    resizeEdge: 'start' | 'end',
    status?: string
  ) => {
    if (!itemId) return;
    startDrag('resize', e.clientX, {
      itemId,
      originalStartDay,
      originalEndDay,
      draggedStatus: status,
      resizeEdge
    });
  };

  // Touch handler for drag-to-move (called from CalendarItem after long-press)
  const handleTouchMoveItem = (
    clientX: number,
    itemId: string,
    originalStartDay: number,
    originalEndDay: number,
    status?: string
  ) => {
    if (!itemId) return;
    const originalStartDate = moment(startDate).add(originalStartDay, 'days');
    const originalEndDate = moment(startDate).add(originalEndDay, 'days');
    const originalWeekdays = countWeekdaysInclusive(originalStartDate, originalEndDate);
    startDrag('move', clientX, { itemId, originalStartDay, originalEndDay, draggedStatus: status, originalWeekdays });
  };

  // Touch handler for resize (called from CalendarItem immediately on handle touch)
  const handleTouchResizeItem = (
    clientX: number,
    itemId: string,
    originalStartDay: number,
    originalEndDay: number,
    resizeEdge: 'start' | 'end',
    status?: string
  ) => {
    if (!itemId) return;
    startDrag('resize', clientX, {
      itemId,
      originalStartDay,
      originalEndDay,
      draggedStatus: status,
      resizeEdge
    });
  };

  // Drag overlay style
  const dragOverlayStyle = useMemo(() => {
    if (!isDragging || !dragState) return {};

    // Show overlay for create, move, or resize mode
    if (dragState.mode === 'create' || dragState.mode === 'move' || dragState.mode === 'resize') {
      let startDayIndex, endDayIndex;
      if (dragState.mode === 'create') {
        startDayIndex = Math.min(dragState.startDay, dragState.currentDay);
        endDayIndex = Math.max(dragState.startDay, dragState.currentDay);
      } else if (dragState.mode === 'move') {
        // Calculate movement in weekdays to avoid jumps when crossing weekends
        const cursorWeekdayDelta = weekdayOrdinal(startDate, dragState.currentDay) - weekdayOrdinal(startDate, dragState.startDay);
        const origStartOrd = weekdayOrdinal(startDate, dragState.originalStartDay ?? 0);
        const newStartOrd = origStartOrd + cursorWeekdayDelta;
        const moveNewStartDay = calendarDayFromWeekdayOrdinal(startDate, newStartOrd);
        if (dragState.originalWeekdays) {
          startDayIndex = moveNewStartDay;
          endDayIndex = calendarDayFromWeekdayOrdinal(startDate, newStartOrd + dragState.originalWeekdays - 1);
        } else {
          const forecastLength = (dragState.originalEndDay ?? 0) - (dragState.originalStartDay ?? 0);
          startDayIndex = moveNewStartDay;
          endDayIndex = moveNewStartDay + forecastLength;
        }
      } else {
        // For resize, determine which edge is being resized
        if (dragState.resizeEdge === 'start') {
          startDayIndex = dragState.currentDay;
          endDayIndex = dragState.originalEndDay ?? 0;
        } else {
          startDayIndex = dragState.originalStartDay ?? 0;
          endDayIndex = dragState.currentDay;
        }
      }
      const style = getDragOverlayStyle(startDayIndex, endDayIndex, startDate, length);
      if ((dragState.mode === 'move' || dragState.mode === 'resize') && dragState.draggedStatus) {
        // Use the solid status color for the border so it's clearly visible (especially for "away"/gray)
        const borderColor = statusColor(dragState.draggedStatus);
        // Use a stronger background than statusBackgroundColor (~0.22 opacity) for better visibility
        const overlayBg: Record<string, string> = {
          '0': 'rgba(64, 166, 218, 0.40)',
          '1': 'rgba(101, 200, 152, 0.40)',
          '2': 'rgba(241, 208, 108, 0.40)',
          '3': 'rgba(251, 137, 137, 0.40)',
        };
        const bg = overlayBg[dragState.draggedStatus] || 'rgba(56, 86, 107, 0.35)';
        return {
          ...style,
          background: bg,
          border: `2px solid ${borderColor}`,
        };
      }
      return style;
    }
    return {};
  }, [isDragging, dragState, startDate, length]);

  return (
    <div
      ref={containerRef}
      className={classes.calendar_row}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ cursor: canDragCreate ? 'crosshair' : 'default' }}
    >
      {/* Day grid */}
      <div className={classes.dayGrid}>
        {[...Array(length)].map((_, i) => (
          <div
            key={i}
            className={`${classes.dayCell} ${isWeekend(startDate, i) ? 'weekend' : ''}`}
            style={{ flex: adjustedDayLength(startDate, i) }}
          />
        ))}
      </div>

      {/* Drag overlay */}
      {isDragging && <div className={classes.dragOverlay} style={dragOverlayStyle} />}

      {/* Calendar items */}
      {user.items.map((item) => {
        const isVacationForecast = item.status === 'away';
        const isStatusForecast = !isVacationForecast;

        // Check if this is a current workload placeholder
        const isCurrentWorkloadPlaceholder = Boolean(item.isCurrentWorkloadPlaceholder);

        const isWfhItem = item.wfhIndex != null;
        const isEditable = (user.isCurrentUser || isAdmin) && !viewOnly && !isWfhItem && !item.externalSource;
        // WFH items are clickable (for deletion) but not draggable/resizable
        const isClickable = isEditable || (isWfhItem && (user.isCurrentUser || isAdmin) && !viewOnly);

        // Don't allow moving placeholders (they represent today), but allow resizing
        const canMove = isEditable && !isCurrentWorkloadPlaceholder;
        const canResize = isEditable;

        return (
          <CalendarItem
            key={item._id || `${item.startDate.valueOf()}-${item.title}`}
            properties={item}
            user={user}
            isEditable={isClickable}
            edit={() => user.id && edit(item, user.id)}
            startDate={startDate}
            length={length}
            dayPixelWidth={dayPixelWidth}
            onMouseDownMove={canMove ? (e, itemId, originalStartDay, originalEndDay) => handleMouseDownMove(e, itemId, originalStartDay, originalEndDay, item.status) : undefined}
            onMouseDownResize={canResize ? (e, itemId, originalStartDay, originalEndDay, resizeEdge) => handleMouseDownResize(e, itemId, originalStartDay, originalEndDay, resizeEdge, item.status) : undefined}
            onTouchMove={canMove ? (clientX, itemId, originalStartDay, originalEndDay) => handleTouchMoveItem(clientX, itemId, originalStartDay, originalEndDay, item.status) : undefined}
            onTouchResize={canResize ? (clientX, itemId, originalStartDay, originalEndDay, resizeEdge) => handleTouchResizeItem(clientX, itemId, originalStartDay, originalEndDay, resizeEdge, item.status) : undefined}
            isBeingDragged={isDragging && ((dragState?.mode === 'move' && dragState.itemId === item._id) || (dragState?.mode === 'resize' && dragState.itemId === item._id))}
          />
        );
      })}
    </div>
  );
};

export default memo(CalendarRow);
